import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { Message, MessageData, MessageDocType, MessageType } from "Types/api/Messages";
import {
    carryOverFromConversations,
    getTranscriptFailure,
    getTranscriptRequest,
    getTranscriptSuccess,
    handlePusherMessage,
    openFromConversations,
    postConversationMessageFailure,
    postConversationMessageRequest,
    postConversationMessageSuccess,
    postConversationStartRequest,
    postStartConversationFailure,
    postStartConversationSuccess,
    toggleChatNotifications,
} from "src/components/Chat/redux/actions";
import { v4 as uuidv4 } from "uuid";

export interface MessageState {
    error: boolean;
    pastTranscripts: Message[];
    transcript: Message[];
    sending: { text: string; tempId: string }[];
    failed: { text: string; tempId: string }[];
    conversations_config: null | {
        channel: string;
        token: string;
    };
    isLeavingMessage: boolean;
    selectedTeam: number | null;
    isOpen: boolean;
    unread: number;
    avatars: {
        [id: string]: string;
    };
}

export type PersistentMessageState = Pick<
    MessageState,
    "conversations_config" | "pastTranscripts" | "avatars" | "selectedTeam"
>;

export const initialMessageState = {
    error: false,
    pastTranscripts: [],
    transcript: [],
    sending: [],
    failed: [],
    conversations_config: null,
    isLeavingMessage: false,
    selectedTeam: null,
    isOpen: false,
    unread: 0,
    avatars: {},
};

function reducer(
    state: MessageState = initialMessageState,
    action:
        | ActionType<typeof postConversationMessageRequest>
        | ActionType<typeof postConversationMessageSuccess>
        | ActionType<typeof postConversationMessageFailure>
        | ActionType<typeof postConversationStartRequest>
        | ActionType<typeof postStartConversationSuccess>
        | ActionType<typeof postStartConversationFailure>
        | ActionType<typeof handlePusherMessage>
        | ActionType<typeof getTranscriptRequest>
        | ActionType<typeof getTranscriptSuccess>
        | ActionType<typeof getTranscriptFailure>
        | ActionType<typeof toggleChatNotifications>
        | ActionType<typeof openFromConversations>
        | ActionType<typeof carryOverFromConversations>
): MessageState {
    switch (action.type) {
        case postConversationStartRequest.type:
            return {
                ...state,
                isLeavingMessage: false,
                error: false,
            };

        case postStartConversationSuccess.type:
            return {
                ...state,
                conversations_config: {
                    token: action.payload.token,
                    channel: action.payload.channel,
                },
            };

        case postStartConversationFailure.type:
            return {
                ...state,
                error: true,
                conversations_config: null,
            };

        case postConversationMessageRequest.type:
            return {
                ...state,
                sending: [...state.sending, action.payload.message],
                failed: state.failed.filter((message) => message.tempId !== action.payload.message.tempId),
            };

        case postConversationMessageSuccess.type:
            // Pusher sometimes beats the leave a message post request back, handle adding pusher message here for correct ordering
            if (action.payload.message.type === MessageType.MessageLeft) {
                return {
                    ...state,
                    conversations_config: null,
                    pastTranscripts: [
                        ...state.pastTranscripts,
                        ...state.transcript,
                        action.payload.message,
                        {
                            displayDate: new Date().toUTCString(),
                            id: uuidv4(),
                            type: MessageType.ConversationEnded,
                        },
                    ],
                    transcript: [],
                    sending: state.sending.filter((message) => message.tempId !== action.payload.tempId),
                    unread: !state.isOpen ? state.unread + 1 : state.unread,
                    isLeavingMessage: false,
                };
            }

            return {
                ...state,
                transcript: [...state.transcript, action.payload.message],
                sending: state.sending.filter((message) => message.tempId !== action.payload.tempId),
            };

        case postConversationMessageFailure.type:
            return {
                ...state,
                conversations_config:
                    action.payload.reason === "Request failed with status code 410" ? null : state.conversations_config,
                sending: state.sending.filter((message) => message.tempId !== action.payload.message.tempId),
                failed: [...state.failed, action.payload.message],
            };

        case handlePusherMessage.type:
            if (action.payload.eventName === "pusher:member_added") {
                return {
                    ...state,
                    avatars: action.payload.data?.id
                        ? {
                              ...state.avatars,
                              [action.payload.data?.id]: action.payload.data?.info?.avatar,
                          }
                        : state.avatars,
                };
            }

            if (
                action.payload.eventName === "visitor-ended" ||
                action.payload.eventName === "bot-conversation-ended" ||
                action.payload.eventName === "conversation-ended" ||
                action.payload.eventName === "close-conversation"
            ) {
                // Pusher sometimes beats the leave a message post request back, handle in post success for correct ordering
                if (
                    state.sending.find((sent) => sent.tempId === MessageType.MessageLeft) ||
                    state.transcript.find((message) => message.type === MessageType.ConversationEnded)
                ) {
                    return state;
                }

                return {
                    ...state,
                    conversations_config: null,
                    pastTranscripts: [
                        ...state.pastTranscripts,
                        ...state.transcript,
                        {
                            displayDate: new Date().toUTCString(),
                            id: uuidv4(),
                            type: MessageType.ConversationEnded,
                        },
                    ],
                    transcript: [],
                    unread: !state.isOpen ? state.unread + 1 : state.unread,
                    isLeavingMessage: false,
                };
            }

            if (action.payload.eventName === "new-bot-message") {
                return {
                    ...state,
                    transcript: [
                        ...state.transcript,
                        {
                            type: action.payload.data.fromType,
                            displayDate: action.payload.data.timestamp,
                            id: action.payload.data.id,
                            message: action.payload.data.text,
                        },
                    ],
                    unread: !state.isOpen ? state.unread + 1 : state.unread,
                };
            }

            if (
                action.payload.eventName === "new-message" &&
                action.payload.data.category === "message" &&
                action.payload.data.fromType !== "visitor"
            ) {
                const attachments = action.payload.data.attachments
                    ? JSON.parse(action.payload.data.attachments)
                    : undefined;

                if (
                    attachments &&
                    !(
                        attachments.some((attachment: any) => attachment.doctype === MessageDocType.Vehicle) ||
                        attachments.some((attachment: any) => attachment.doctype === MessageDocType.Generic) ||
                        attachments.some((attachment: any) => attachment.doctype === MessageDocType.Video)
                    )
                ) {
                    return state;
                }

                return {
                    ...state,
                    transcript: [
                        ...state.transcript,
                        {
                            displayDate: action.payload.data.timestamp,
                            id: action.payload.data.messageId,
                            message: action.payload.data.messageText,
                            type: action.payload.data.fromType,
                            avatar: state.avatars[`agent-${action.payload.data.fromId}`],
                            attachments: attachments,
                        },
                    ],
                    unread: !state.isOpen ? state.unread + 1 + (attachments ?? []).length : state.unread,
                };
            }

            if (action.payload.eventName === "conversation.leave-a-message") {
                return {
                    ...state,
                    transcript: [
                        ...state.transcript,
                        {
                            displayDate: new Date().toUTCString(),
                            id: uuidv4(),
                            type: MessageType.LeaveAMessage,
                        },
                    ],
                    isLeavingMessage: true,
                };
            }

            if (action.payload.eventName === "started-real-convo") {
                return {
                    ...state,
                    selectedTeam: action.payload.data.teamId,
                };
            }

            return state;

        case getTranscriptRequest.type:
            return {
                ...state,
            };

        case getTranscriptSuccess.type:
            const newTranscript: MessageData["messages"] = [];

            for (const rawMessage of action.payload.transcript
                .filter(
                    (message) =>
                        !(
                            message.body.includes("Online Shopper Garage") &&
                            message.body.includes("They would like to chat!")
                        )
                )
                .filter(
                    (message) =>
                        !message?.attachments ||
                        message?.attachments.length === 0 ||
                        message.attachments.some((attachment: any) => attachment.doctype === MessageDocType.Vehicle) ||
                        message.attachments.some((attachment: any) => attachment.doctype === MessageDocType.Generic) ||
                        message.attachments.some((attachment: any) => attachment.doctype === MessageDocType.Video)
                )) {
                if (rawMessage.category !== "message") {
                    continue;
                }

                newTranscript.push({
                    id: rawMessage.messageId,
                    displayDate: rawMessage.timestamp,
                    message: rawMessage.body,
                    attachments:
                        rawMessage.attachments && rawMessage.attachments.length ? rawMessage.attachments : undefined,
                    avatar: rawMessage.agentId ? state.avatars[`agent-${rawMessage.agentId}`] : undefined,
                    type:
                        rawMessage.visitorId !== null
                            ? MessageType.Visitor
                            : rawMessage.agentId === null && rawMessage.type === "bot-to-visitor"
                            ? MessageType.Bot
                            : MessageType.Agent,
                });
            }

            return {
                ...state,
                conversations_config: action.payload.endedAt ? null : state.conversations_config,
                transcript: action.payload.endedAt ? [] : newTranscript,
                pastTranscripts: action.payload.endedAt
                    ? [...state.pastTranscripts, ...newTranscript]
                    : state.pastTranscripts,
            };

        case getTranscriptFailure.type:
            return {
                ...initialMessageState,
            };

        case toggleChatNotifications.type:
            return {
                ...state,
                isOpen: !state.isOpen,
                unread: 0,
            };

        case openFromConversations.type:
            return {
                ...initialMessageState,
                isOpen: true,
                conversations_config: {
                    token: action.payload.token,
                    channel: action.payload.channel,
                },
            };

        case carryOverFromConversations.type:
            return {
                ...initialMessageState,
                conversations_config: {
                    token: action.payload.token,
                    channel: action.payload.channel,
                },
            };

        default:
            return state;
    }
}

export default reducer;
